"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@shared/src/components/ui/avatar";
import { Button } from "@shared/src/components/ui/button";
import { FaArrowRight, FaClock, FaThumbsUp } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { AD_QUERY_KEY } from "@shared/src/lib/queryKeys";
import { NetworkChip } from "@shared/src/components/ui/network-chip";
import MerchantFoundSkeleton from "@shared/src/components/ui/common/skeletons/MerchantFoundSkeleton";
import { useQuickTradeFlowStore } from "@shared/src/stores/useQuickTradeFlowStore";
import { AdNotFound } from "@shared/src/components/modules/quick-trade/AdNotFound";
import { CountdownTimer } from "../../common/misc/CountdownTimer";
import { ProgressWithTimer } from "../../common/misc/ProgressWithTimer";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";
import { useQuickTradeOptions } from "@shared/src/hooks/quick-trade/useQuickTradeOptions";
import { useEffect } from "react";
import { ORDER_PAGE } from "@shared/src/config/page-links";

interface QuickTradeMerchantProps {
  onCancel: () => void;
  paymentMethod: string;
  orderQuantity: number;
  traderChainId: number;
}

export default function MerchantFound({ onCancel, paymentMethod, traderChainId }: QuickTradeMerchantProps) {
  const sdk = useHostStore(state => state.sdk);
  const router = useHostStore(state => state.router);
  const adId = useQuickTradeFlowStore(state => state.merchantAdId);
  const quickTrade = useNotificationStore(state => state.quickTradeNotification?.quickTrade);
  const { quickTradeWaitTime = 0 } = useQuickTradeOptions();
    console.log("quickTradeWaitTime", quickTradeWaitTime, sdk);

  // Calculate expiry time
  const expiryTime = new Date((quickTrade?.lastContactedTime || 0) + quickTradeWaitTime);

  const orderHash2 = useNotificationStore.getState().quickTradeNotification?.orderHash;

  useEffect(() => {
    router.prefetch(ORDER_PAGE("placeholder"));
    const orderHash = useNotificationStore.getState().quickTradeNotification?.orderHash;
    if (orderHash) {
      useQuickTradeFlowStore.getState().goToForm();
    }
  }, []);

  const { data: ad, isLoading: isAdLoading } = useQuery({
    queryKey: AD_QUERY_KEY(adId || ""),
    queryFn: async () => {
      const res = adId ? await sdk?.ads.getAdById(adId) : null;
      if (!res?.data) {
        throw new Error("Ad not found");
      }
      if (res.error) {
        throw res.error;
      }
      return res.data;
    },
    enabled: !!adId,
  });
  console.log("isAdLoading", isAdLoading, adId, ad);
  if (!ad && isAdLoading) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto">
        <MerchantFoundSkeleton />
      </div>
    );
  }
  if (!ad) {
    return <AdNotFound onBack={onCancel} />;
  }
  console.log("prefetch order page", { quickTrade, ad });

  const name = ad.merchant.account?.username || ad.merchant.address;

  async function handleComplete() {
    setTimeout(async () => {
      if (!quickTrade?.id) return;
      const data = await sdk?.orders.getUserQuickTrade();
      const quickTradeData = data?.data;
      if (quickTradeData?.quickTrade) {
        useNotificationStore.getState().setQuickTradeNotification(quickTradeData);
      }
    }, 1000);
  }
  return (
    <div className="flex flex-col w-full max-w-lg mx-auto">
      <div className="text-center space-y-2">
        <h2 className="text-h4 font-medium text-text-primary">We&apos;ve Found a merchant</h2>
        <p className="text-text-secondary">Waiting for Merchant to respond...</p>
        <div className="flex flex-col items-center justify-center gap-2">
          <CountdownTimer
            onComplete={handleComplete}
            className="py-2 px-4 rounded-2xl bg-surfaces-bg-secondary w-fit"
            targetDate={expiryTime}
          />

          <ProgressWithTimer targetDate={expiryTime} totalTime={quickTradeWaitTime} />
        </div>
      </div>

      {/* Trade Details Card */}
      <div className="border-2 border-border-secondary rounded-3xl p-4 sm:p-6 w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4 sm:gap-0">
          <h6 className={`font-medium ${ad.type === "buy" ? "text-text-success" : "text-text-error"}`}>
            {ad.type === "buy" ? "Buy" : "Sell"} <span className="text-text-tertiary">{ad.tokenDetails.symbol}</span>
          </h6>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1 w-full sm:w-auto">
              <NetworkChip networkId={ad.networkId} />
              <FaArrowRight className="text-icons-icon-tertiary shrink-0" />
              <NetworkChip networkId={traderChainId} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Fiat Amount */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Fiat Amount</p>
            <p className="text-text-primary">
              {ad.quantity * ad.price} {ad.currencyId}
            </p>
          </div>

          {/* Price */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Price</p>
            <p className="text-text-primary">
              {ad.price} {ad.currencyId}
            </p>
          </div>

          {/* Total Quantity */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Total Quantity</p>
            <p className="text-text-primary">
              {ad.quantity} {ad.tokenDetails.symbol}
            </p>
          </div>

          {/* Payment Method */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Payment Method</p>
            <p className="text-text-primary">{paymentMethod}</p>
          </div>
        </div>
      </div>

      {/* Merchant Details Card */}
      <div className="flex-1 bg-surfaces-bg-secondary-darker p-3 md:p-6 rounded-3xl flex flex-col">
        {/* Merchant Header */}
        <div className="flex items-center mb-4">
          <div className="relative">
            <Avatar className="h-8 w-8 rounded-xl ">
              <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`} />
              <AvatarFallback className="text-small">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="absolute bottom-0 right-0 size-1.5 rounded-full bg-text-success ring-2 ring-surfaces-bg-primary" />
          </div>
          <div className="ml-4">
            <p className="font-extrabold text-text-primary">{name}</p>
            <p className="text-small">
              <span>{ad.merchantStats?.totalOrders}</span> | <span>{ad.processedStats?.completionRate}</span>
            </p>
            <div className="flex items-center gap-2 text-text-secondary text-small">
              <div className="flex items-center gap-1">
                <FaThumbsUp size={12} />
                <span>{ad.processedStats?.positiveRates}</span>
              </div>
              <div className="flex items-center gap-1">
                <FaClock size={12} />
                <span>{ad.timeLimit}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Info */}
        <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <div>
            <p className="block text-text-primary font-extrabold">{ad.timeLimit / 60} min</p>
            <p className="block text-small text-text-secondary">Payment Time Limit</p>
          </div>
          <div>
            <p className="block text-text-primary font-extrabold">{ad.responseTime}</p>
            <p className="block text-small text-text-secondary">Avg. Release Time</p>
          </div>
          <div className="col-span-2 md:col-span-1">
            <div className="flex gap-2">
              <p className="block text-text-primary font-extrabold flex-shrink-0">
                {ad.quantity} {ad.tokenDetails.symbol}{" "}
              </p>
            </div>
            <p className="block text-small text-text-secondary">Available</p>
          </div>
        </div>

        {/* Advertiser Terms */}
        <fieldset className="flex-1 p-4 rounded-2xl border border-border-tertiary bg-black text-text-primary">
          <legend className="px-4 py-1 text-small font-semibold text-text-secondary bg-surfaces-bg-primary rounded-full">
            Advertisers Terms
          </legend>
          <div className="text-small-2 font-semibold text-text-secondary mt-2 flex flex-col gap-2">
            {ad.tags.map((term, index) => (
              <span key={index}>{term}</span>
            ))}
          </div>
        </fieldset>
      </div>
      <Button variant="outline" size="cta" className="w-full" onClick={onCancel} disabled={isAdLoading}>
        Cancel
      </Button>
    </div>
  );
}
